import React, { useEffect } from "react";
import { Alert, Box, Button, Paper, Typography, useTheme } from "@mui/material";
import { currentUser, permissionTest, UserPreference } from "system/InfegyUser";
import AIGradientTextField from "components/design/AI/AIGradientTextField";
import BooleanQueryField from "components/queries/BooleanQueryField";
import { useTranslation } from "react-i18next";
import DashboardSelectionButtonPreloaded from "components/resources/dashboards/DashboardSelectionButtonPreloaded";
import RecentQuickSearches from "./RecentQuickSearches";
import { useNavigate } from "react-router-dom";
import QuickSearchActions from "./QuickSearchActions";
import { submitTemplateQueries, TemplateQueryProps } from "./TemplateSearchContent";
import { socialDatasetId } from "system/query/Query";
import APIRequest, { APIError } from "system/APIRequest";
import AIInlineLoader from "components/design/AI/AIInlineLoader";
import { APIErrorAlert } from "components/apiHandling/APIErrorAlert";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";

export default function SimplifiedQuickSearch(): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const user = currentUser();

  sessionStorage.removeItem("skipQuickSearchRedirect");

  const [showRecentSearches, setShowRecentSearches] = React.useState<boolean>(false);
  const [editorContent, setEditorContent] = React.useState<string>("");
  const [selectedDashboardOption, setSelectedDashboardOption] = React.useState<DashboardDocument | undefined>(undefined);
  const [overrideAIForm, setOverrideAIForm] = React.useState<boolean>(false);
  const [queryInfoError, setQueryInfoError] = React.useState<APIError | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [hasMoreThanOneDashboard, setHasMoreThanOneDashboard] = React.useState<boolean>(false);

  const userPrefersBoolean = user?.getPreference(UserPreference.DEFAULT_QUICKSEARCH_VS_AI) === "on";

  const usingAI = permissionTest("ai_features") && !overrideAIForm && !userPrefersBoolean;

  const randomPrompt = "What would you like to know more about?";

  useEffect(() => {
    // If we don't have a selected dashboard, load the most recently updated template dashboard
    if (!selectedDashboardOption) {
      const requestOptions:any = {
        "per_page":2,
        "sort":"last_viewed_by_you",
        "sort_order":"desc",
        "include_shared":1
      };
      requestOptions["q"] = JSON.stringify([{"id":"template_type","op":"not-null","value":""}]);

      APIRequest("obj/dashboard", requestOptions).then((response: any) => {
        if (response?.output.length) {
          setSelectedDashboardOption(response.output[0]);
          setHasMoreThanOneDashboard(response.output.length > 1);
        } else {
          const searchObj = [
            {"id":"global_access","op":"=","value":"1"},
            {"id":"template_type","op":"not-null","value":""}
          ];

          const globalRequestOptions:any = {
            "per_page":1,
            "sort":"last_viewed_by_you",
            "sort_order":"desc",
            "include_shared": 1,
            "q": JSON.stringify(searchObj)
          };

          APIRequest("obj/dashboard", globalRequestOptions).then((response: any) => {
            if (response?.output.length) {
              setSelectedDashboardOption(response.output[0]);
            }
          });
        }
      });
    }
  }, [selectedDashboardOption]);

  const submitQueries = () => {
    const content = editorContent.trim();
    if (!selectedDashboardOption?.id || !content) {
      return;
    }

    const newTemplateQuery : TemplateQueryProps = new TemplateQueryProps(user);
    newTemplateQuery.queryTitle = "";
    newTemplateQuery.quickSearchContent = content;
    newTemplateQuery.usingAIQueryBuilder = usingAI;
    newTemplateQuery.searchFieldIds = ["body", "title", "description"];

    setIsLoading(true);

    submitTemplateQueries({
      datasetId: socialDatasetId,
      dashboardId: selectedDashboardOption?.id,
      fieldIds: ["body", "title", "description"],
      templateQueries: [newTemplateQuery],
      setQueryInfoError: setQueryInfoError,
      setIsLoading: setIsLoading,
      navigate: navigate,
      user: user
    }).then(() => {
      setIsLoading(false);
    }).catch((error) => {
      setQueryInfoError(error as APIError);
      setIsLoading(false);
    });
  }

  return (
  <Box sx={{ width: "100%", maxWidth: "1000px", mt: 5, mb: 5, p: 2 }}>
    <Paper sx={{ px: 2, py: 1, position: "relative" }}>
      {isLoading && (<>
        <Box sx={{ position: "absolute", top: "0px", left: "0px", right: "0px", bottom: "0px", backgroundColor: theme.palette.background.paper, opacity: 0.5}} />
        <Box sx={{ position: "absolute", top: "0px", left: "0px", right: "0px", bottom: "0px", backdropFilter: "blur(3px)", textAlign: "center", alignContent: "center", zIndex: 999 }}>
          <AIInlineLoader />
        </Box>
      </>)}
      <Typography variant="h2" sx={{ textAlign:"center", mt:1, mb:2.5, color: theme.palette.text.secondary, opacity: 0.7, fontWeight: "400" }}>
        {randomPrompt}
      </Typography>
      <Box>
        {usingAI && (
          <AIGradientTextField
            hideGradient={!usingAI}
            value={editorContent}
            onSubmit={submitQueries}
            submitOnEnter={usingAI}
            onChange={(e) => {
              setEditorContent(e.target.value);
            }}
            placeholder={usingAI ? i18n.t('ai-query.ai-query-prompt-placeholder') : i18n.t('widget-types.template-search.query-placeholder')} 
            actions={(
              <QuickSearchActions
                editorContent={editorContent}
                selectedDashboardOption={selectedDashboardOption}
                isUsingAIForm={usingAI}
                onSubmit={submitQueries}
                setOverrideAIForm={setOverrideAIForm} />
            )} />
        )}
        {!usingAI && (
          <BooleanQueryField
            height={30}
            content={editorContent}
            setContent={(content: string) => {
              setEditorContent(content);
            }}
            placeholder={i18n.t('widget-types.template-search.query-placeholder')}
            actions={(
              <QuickSearchActions
                editorContent={editorContent}
                selectedDashboardOption={selectedDashboardOption}
                isUsingAIForm={usingAI}
                onSubmit={submitQueries}
                setOverrideAIForm={setOverrideAIForm} />
            )} />
        )}
      </Box>
      <Alert severity="info" sx={{mt: 2}}>
        {i18n.t("start-page.quick-search-intro")}
        <a href="/me">{i18n.t("profile-page.preferences-heading")}</a>.

      </Alert>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1, opacity: 0.4, "&:hover": { opacity: 1 } }}>
        <Box>
          {hasMoreThanOneDashboard &&
            <DashboardSelectionButtonPreloaded
              selectedDashboard={selectedDashboardOption}
              setSelectedDashboard={setSelectedDashboardOption}
              limitToTemplateDashboards={true}
              size="small"
              variant="text"
            />
          }
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Button
            variant="text"
            size='small'
            sx={{ color: theme.palette.text.secondary, textTransform: "none", fontSize: "0.8rem" }}
            onClick={() => {
              setShowRecentSearches(!showRecentSearches);
            }}>
            {showRecentSearches ? i18n.t('widget-types.template-search.hide-recent-searches') : i18n.t('widget-types.template-search.show-recent-searches')}
          </Button>
        </Box>
      </Box>
      {queryInfoError &&
        <APIErrorAlert fetchError={queryInfoError}></APIErrorAlert>
      }

    </Paper>
    {!!showRecentSearches && (
      <Box sx={{ mt: 4 }}>
        <RecentQuickSearches />
      </Box>
    )}
  </Box>
  );
}
